// Global Instructions Rule Applied!
//
// Shared asset-transactions table for the deal form's Execution and Wire
// tabs (full-width detail set — owner refinement 2026-07-27). Rows are
// chronological (the RPC orders ASC) and the date shows the time because
// same-day transaction sequence matters. Tranche Affected sits on the far
// right and links to the Tranche form (house link styling, not hover-only).
// No./Principal are the acquired-or-disposed coalesced values
// from the RPC; Unit Value (base) is price_functional in the firm base
// currency (symbol from the currencies config, is_base row).
import React from 'react'
import { Table, Tag } from 'antd'
import dayjs from 'dayjs'
import { useCurrencies } from '../../currencies/useCurrencies.ts'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { DealExecutionTransaction } from '../../../state_manager/data_models/deal_execution_board_model.ts'

const DATETIME_FORMAT = 'MM/DD/YYYY HH:mm'

interface DealTransactionsTableProps {
  transactions: DealExecutionTransaction[]
  currencySymbol: string | null | undefined
  emptyText: string
}

// Accounting amounts show either no decimals or exactly two — never the
// bare toLocaleString single-decimal look.
function money(symbol: string | null | undefined, value: unknown): string {
  if (value == null) return '—'
  const n = Number(value)
  const digits = Number.isInteger(n) ? 0 : 2
  return `${symbol || ''}${n.toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  })}`
}

function unitValue(symbol: string | null | undefined, value: unknown): string {
  if (value == null) return '—'
  return `${symbol || ''}${Number(value).toLocaleString('en-US', { maximumFractionDigits: 4 })}`
}

export default function DealTransactionsTable({
  transactions,
  currencySymbol,
  emptyText
}: DealTransactionsTableProps): React.ReactElement {
  const { openFormInTab } = useTabManagerContext()
  const { currencies } = useCurrencies()
  const baseSymbol = currencies.find((c) => c.is_base)?.symbol || currencySymbol

  const recordLink = (
    label: string,
    onClick: () => void
  ): React.ReactElement => (
    <button
      type='button'
      className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
      onClick={onClick}
    >
      {label}
    </button>
  )

  if (transactions.length === 0) {
    return <div className='text-sm text-gray-400 py-2'>{emptyText}</div>
  }
  return (
    <Table
      size='small'
      rowKey='asset_transactions__id'
      pagination={false}
      dataSource={transactions}
      scroll={{ x: true }}
      columns={[
        {
          title: 'Date',
          dataIndex: 'asset_transactions__transaction_date',
          // wide enough for MM/DD/YYYY HH:mm on one line; slice strips the
          // zone offset so the stored wall-clock time shows verbatim (same
          // convention as the booking form — no browser-timezone shift)
          width: 135,
          render: (v) => (
            <span className='whitespace-nowrap'>
              {v ? dayjs(String(v).slice(0, 19)).format(DATETIME_FORMAT) : '—'}
            </span>
          )
        },
        {
          // Opens the full booking form — read-only when Booked, with the
          // record actions (Trash) available there.
          title: 'Transaction',
          dataIndex: 'asset_transactions__name',
          render: (v, row) =>
            recordLink(
              (v as string) || row.asset_transaction_types__label || 'Transaction',
              () =>
                openFormInTab(
                  'asset-transactions',
                  row.asset_transactions__id,
                  (v as string) || row.asset_transaction_types__label || 'Transaction'
                )
            )
        },
        { title: 'Type', dataIndex: 'asset_transaction_types__label', render: (v) => v || '—' },
        {
          title: 'Fund',
          dataIndex: 'funds__name',
          render: (v, row) =>
            row.funds__id
              ? recordLink((v as string) || 'Fund', () =>
                  openFormInTab('funds', row.funds__id as string, (v as string) || 'Fund')
                )
              : v || '—'
        },
        {
          title: 'Security',
          dataIndex: 'securities__name',
          render: (v, row) =>
            row.securities__id
              ? recordLink((v as string) || 'Security', () =>
                  openFormInTab('securities', row.securities__id as string, (v as string) || 'Security')
                )
              : v || '—'
        },
        {
          title: 'Status',
          dataIndex: 'transaction_statuses__label',
          render: (v) => (v ? <Tag color={v === 'Booked' ? 'green' : 'blue'}>{v}</Tag> : '—')
        },
        {
          title: 'Amount',
          dataIndex: 'asset_transactions__amount',
          align: 'right' as const,
          render: (v) => money(currencySymbol, v)
        },
        {
          title: 'No.',
          dataIndex: 'asset_transactions__no',
          align: 'right' as const,
          render: (v) => (v != null ? Number(v).toLocaleString('en-US') : '—')
        },
        {
          title: 'Principal',
          dataIndex: 'asset_transactions__principal',
          align: 'right' as const,
          render: (v) => money(currencySymbol, v)
        },
        {
          title: 'Unit Value',
          dataIndex: 'asset_transactions__price',
          align: 'right' as const,
          render: (v) => unitValue(currencySymbol, v)
        },
        {
          title: 'Unit Value (base)',
          dataIndex: 'asset_transactions__price_functional',
          align: 'right' as const,
          render: (v) => unitValue(baseSymbol, v)
        },
        {
          title: 'Tranche Affected',
          dataIndex: 'tranches__name',
          render: (v, row) =>
            row.tranches__id ? (
              recordLink((v as string) || 'Tranche', () =>
                openFormInTab('tranches', row.tranches__id as string, (v as string) || 'Tranche')
              )
            ) : (
              <span className='text-gray-300'>—</span>
            )
        }
      ]}
    />
  )
}
